import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Settings, Send, CheckCircle, XCircle, Loader2, AlertCircle, History, Wifi, Plus, Trash2, Building2, Edit } from 'lucide-react';
import { toast } from 'sonner';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const BRANCHES = ['KISAASI', 'BUNGA', 'KIGALI', 'LUBOWA'];

const TallySetupPage = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [configs, setConfigs] = useState([]);
  const [postingLogs, setPostingLogs] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [posting, setPosting] = useState({});
  const [testing, setTesting] = useState({});

  const [newConfig, setNewConfig] = useState({
    name: '',
    tally_type: 'A',
    company_name: '',
    url: '',
    branches: [],
    is_active: true
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [configsRes, logsRes] = await Promise.all([
        api.get('/tally/configs'),
        api.get('/tally/posting-logs?limit=50')
      ]);
      setConfigs(configsRes.data);
      setPostingLogs(logsRes.data);
    } catch (err) {
      toast.error('Failed to load Tally configurations');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [fetchData, isAdmin]);

  const handleBranchToggle = (branch) => {
    const current = newConfig.branches;
    if (current.includes(branch)) {
      setNewConfig({ ...newConfig, branches: current.filter(b => b !== branch) });
    } else {
      setNewConfig({ ...newConfig, branches: [...current, branch] });
    }
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    if (newConfig.branches.length === 0) {
      toast.error('Please select at least one branch');
      return;
    }
    setSaving(true);
    try {
      if (editingConfig) {
        await api.put(`/tally/configs/${editingConfig.id}`, newConfig);
        toast.success('Configuration updated');
      } else {
        await api.post('/tally/configs', newConfig);
        toast.success('Configuration created');
      }
      setShowAddDialog(false);
      setEditingConfig(null);
      setNewConfig({ name: '', tally_type: 'A', company_name: '', url: '', branches: [], is_active: true });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleEditConfig = (config) => {
    setEditingConfig(config);
    setNewConfig({
      name: config.name,
      tally_type: config.tally_type,
      company_name: config.company_name,
      url: config.url,
      branches: config.branches || [],
      is_active: config.is_active
    });
    setShowAddDialog(true);
  };

  const handleDeleteConfig = async (configId) => {
    if (!window.confirm('Are you sure you want to delete this configuration?')) return;
    try {
      await api.delete(`/tally/configs/${configId}`);
      toast.success('Configuration deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const testConnection = async (configId) => {
    setTesting({ ...testing, [configId]: true });
    try {
      const response = await api.post(`/tally/configs/${configId}/test`);
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      toast.error(`Connection test failed: ${err.message}`);
    } finally {
      setTesting({ ...testing, [configId]: false });
    }
  };

  const postToTally = async (configId, configName) => {
    setPosting({ ...posting, [configId]: true });
    try {
      const response = await api.post('/tally/post-branch', {
        config_id: configId,
        start_date: null,
        end_date: null
      });
      if (response.data.success) {
        toast.success(`${response.data.message} - ${response.data.posted_count} transactions`);
        fetchData();
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post to Tally');
    } finally {
      setPosting({ ...posting, [configId]: false });
    }
  };

  const getAssignedBranches = (tallyType, excludeConfigId = null) => {
    return configs
      .filter(c => c.tally_type === tallyType && c.id !== excludeConfigId)
      .flatMap(c => c.branches || []);
  };

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <AlertCircle className="w-12 h-12 text-[#C85A40] mb-4" />
        <p className="text-[#5C635F]">Admin access required</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#3E3F96]" />
      </div>
    );
  }

  const tallyAConfigs = configs.filter(c => c.tally_type === 'A');
  const tallyBConfigs = configs.filter(c => c.tally_type === 'B');

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1C1F1D]">Tally Integration</h1>
          <p className="text-[#5C635F] mt-1">Configure multi-branch Tally posting</p>
        </div>
        
        <Dialog open={showAddDialog} onOpenChange={(open) => {
          setShowAddDialog(open);
          if (!open) {
            setEditingConfig(null);
            setNewConfig({ name: '', tally_type: 'A', company_name: '', url: '', branches: [], is_active: true });
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-[#3E3F96] hover:bg-[#2D2E7A]" data-testid="add-tally-config-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Configuration
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingConfig ? 'Edit Configuration' : 'Add Tally Configuration'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSaveConfig} className="space-y-4">
              <div className="space-y-2">
                <Label>Configuration Name</Label>
                <Input 
                  placeholder="e.g., Tally A - Uganda Branches"
                  value={newConfig.name}
                  onChange={(e) => setNewConfig({ ...newConfig, name: e.target.value })}
                  required
                  data-testid="config-name-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tally Type</Label>
                  <Select 
                    value={newConfig.tally_type} 
                    onValueChange={(v) => setNewConfig({ ...newConfig, tally_type: v, branches: [] })}
                  >
                    <SelectTrigger data-testid="tally-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A">
                        <div className="flex items-center gap-2">
                          <Badge className="bg-[#3E3F96]/10 text-[#3E3F96]">A</Badge>
                          All Transactions
                        </div>
                      </SelectItem>
                      <SelectItem value="B">
                        <div className="flex items-center gap-2">
                          <Badge className="bg-[#D99C3B]/10 text-[#D99C3B]">B</Badge>
                          Bank Only (No Cash)
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Status</Label>
                  <div className="flex items-center gap-2 pt-2">
                    <Switch 
                      checked={newConfig.is_active}
                      onCheckedChange={(v) => setNewConfig({ ...newConfig, is_active: v })}
                    />
                    <span className="text-sm text-[#5C635F]">{newConfig.is_active ? 'Active' : 'Inactive'}</span>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Tally Company Name</Label>
                <Input 
                  placeholder="Company name in Tally"
                  value={newConfig.company_name}
                  onChange={(e) => setNewConfig({ ...newConfig, company_name: e.target.value })}
                  required
                  data-testid="company-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Tally URL</Label>
                <Input 
                  placeholder="http://localhost:9000"
                  value={newConfig.url}
                  onChange={(e) => setNewConfig({ ...newConfig, url: e.target.value })}
                  required
                  data-testid="tally-url-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Branches</Label>
                <p className="text-xs text-[#5C635F]">Select branches that will post to this Tally</p>
                <div className="grid grid-cols-2 gap-2 p-3 bg-[#F5F5FA] rounded-lg">
                  {BRANCHES.map((branch) => {
                    const assignedTo = getAssignedBranches(newConfig.tally_type, editingConfig?.id);
                    const isAssigned = assignedTo.includes(branch);
                    const isSelected = newConfig.branches.includes(branch);
                    
                    return (
                      <div key={branch} className="flex items-center gap-2">
                        <Checkbox 
                          id={`branch-${branch}`}
                          checked={isSelected}
                          disabled={isAssigned && !isSelected}
                          onCheckedChange={() => handleBranchToggle(branch)}
                        />
                        <label 
                          htmlFor={`branch-${branch}`} 
                          className={`text-sm cursor-pointer ${isAssigned && !isSelected ? 'text-[#5C635F] line-through' : ''}`}
                        >
                          {branch}
                          {isAssigned && !isSelected && <span className="text-xs ml-1">(assigned)</span>}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Posting Rules Info */}
              <div className="p-3 bg-[#ECEDF5] rounded-lg text-sm">
                <p className="font-medium text-[#1C1F1D] mb-2">Posting Rules for Tally {newConfig.tally_type}:</p>
                {newConfig.tally_type === 'A' ? (
                  <ul className="text-[#5C635F] space-y-1">
                    <li>• Invoices</li>
                    <li>• Payments (Cash)</li>
                    <li>• Credit Notes (Approved)</li>
                    <li>• Bank Reconciliation</li>
                  </ul>
                ) : (
                  <ul className="text-[#5C635F] space-y-1">
                    <li>• Invoices</li>
                    <li>• Credit Notes (Approved)</li>
                    <li>• Bank Reconciliation</li>
                    <li className="text-[#C85A40]">• NO Cash Payments</li>
                  </ul>
                )}
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#3E3F96] hover:bg-[#2D2E7A]" disabled={saving} data-testid="save-config-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  {editingConfig ? 'Update' : 'Create'} Configuration
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-[#D8D9E8] border-l-4 border-l-[#3E3F96]">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Badge className="bg-[#3E3F96]/10 text-[#3E3F96]">Tally A</Badge>
              All Transactions
            </CardTitle>
            <CardDescription>Invoices, Payments, Credit Notes, Bank Recon</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-[#3E3F96]">{tallyAConfigs.length}</p>
            <p className="text-sm text-[#5C635F]">configurations</p>
          </CardContent>
        </Card>
        
        <Card className="border-[#D8D9E8] border-l-4 border-l-[#D99C3B]">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Badge className="bg-[#D99C3B]/10 text-[#D99C3B]">Tally B</Badge>
              Bank Only
            </CardTitle>
            <CardDescription>No Cash Payments</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-[#D99C3B]">{tallyBConfigs.length}</p>
            <p className="text-sm text-[#5C635F]">configurations</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="configs" className="space-y-4">
        <TabsList className="bg-[#ECEDF5]">
          <TabsTrigger value="configs" data-testid="tab-configs">
            <Settings className="w-4 h-4 mr-2" />
            Configurations
          </TabsTrigger>
          <TabsTrigger value="post" data-testid="tab-post">
            <Send className="w-4 h-4 mr-2" />
            Post to Tally
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <History className="w-4 h-4 mr-2" />
            Posting History
          </TabsTrigger>
        </TabsList>

        {/* Configurations Tab */}
        <TabsContent value="configs">
          {configs.length === 0 ? (
            <Card className="border-[#D8D9E8]">
              <CardContent className="flex flex-col items-center justify-center h-64">
                <Building2 className="w-12 h-12 text-[#5C635F] opacity-50 mb-4" />
                <p className="text-[#5C635F]">No Tally configurations yet</p>
                <p className="text-sm text-[#5C635F]">Click "Add Configuration" to get started</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Tally A Configs */}
              {tallyAConfigs.length > 0 && (
                <Card className="border-[#D8D9E8]">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Badge className="bg-[#3E3F96]/10 text-[#3E3F96]">Tally A</Badge>
                      Configurations
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#F5F5FA]">
                          <TableHead className="text-xs uppercase font-semibold">Name</TableHead>
                          <TableHead className="text-xs uppercase font-semibold">Company</TableHead>
                          <TableHead className="text-xs uppercase font-semibold">Branches</TableHead>
                          <TableHead className="text-xs uppercase font-semibold">Status</TableHead>
                          <TableHead className="text-xs uppercase font-semibold">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {tallyAConfigs.map((config) => (
                          <TableRow key={config.id}>
                            <TableCell className="font-medium">{config.name}</TableCell>
                            <TableCell className="text-[#5C635F]">{config.company_name}</TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {config.branches?.map((b) => (
                                  <Badge key={b} variant="outline" className="text-xs">{b}</Badge>
                                ))}
                              </div>
                            </TableCell>
                            <TableCell>
                              {config.is_active ? (
                                <Badge className="bg-[#5556A8]/10 text-[#5556A8]">Active</Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-600">Inactive</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => testConnection(config.id)}
                                  disabled={testing[config.id]}
                                >
                                  {testing[config.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wifi className="w-4 h-4" />}
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleEditConfig(config)}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" className="text-[#C85A40]" onClick={() => handleDeleteConfig(config.id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
              
              {/* Tally B Configs */}
              {tallyBConfigs.length > 0 && (
                <Card className="border-[#D8D9E8]">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Badge className="bg-[#D99C3B]/10 text-[#D99C3B]">Tally B</Badge>
                      Configurations
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#F5F5FA]">
                          <TableHead className="text-xs uppercase font-semibold">Name</TableHead>
                          <TableHead className="text-xs uppercase font-semibold">Company</TableHead>
                          <TableHead className="text-xs uppercase font-semibold">Branches</TableHead>
                          <TableHead className="text-xs uppercase font-semibold">Status</TableHead>
                          <TableHead className="text-xs uppercase font-semibold">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {tallyBConfigs.map((config) => (
                          <TableRow key={config.id}>
                            <TableCell className="font-medium">{config.name}</TableCell>
                            <TableCell className="text-[#5C635F]">{config.company_name}</TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {config.branches?.map((b) => (
                                  <Badge key={b} variant="outline" className="text-xs">{b}</Badge>
                                ))}
                              </div>
                            </TableCell>
                            <TableCell>
                              {config.is_active ? (
                                <Badge className="bg-[#5556A8]/10 text-[#5556A8]">Active</Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-600">Inactive</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => testConnection(config.id)}
                                  disabled={testing[config.id]}
                                >
                                  {testing[config.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wifi className="w-4 h-4" />}
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleEditConfig(config)}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" className="text-[#C85A40]" onClick={() => handleDeleteConfig(config.id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        {/* Post to Tally Tab */}
        <TabsContent value="post">
          {configs.length === 0 ? (
            <Card className="border-[#D8D9E8]">
              <CardContent className="flex flex-col items-center justify-center h-64">
                <AlertCircle className="w-12 h-12 text-[#D99C3B] mb-4" />
                <p className="text-[#5C635F]">No Tally configurations available</p>
                <p className="text-sm text-[#5C635F]">Please add configurations first</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {configs.filter(c => c.is_active).map((config) => (
                <Card key={config.id} className="border-[#D8D9E8]">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Badge className={config.tally_type === 'A' ? 'bg-[#3E3F96]/10 text-[#3E3F96]' : 'bg-[#D99C3B]/10 text-[#D99C3B]'}>
                          Tally {config.tally_type}
                        </Badge>
                        {config.name}
                      </CardTitle>
                    </div>
                    <CardDescription>{config.company_name}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-sm text-[#5C635F] mb-2">Branches:</p>
                      <div className="flex flex-wrap gap-1">
                        {config.branches?.map((b) => (
                          <Badge key={b} variant="outline">{b}</Badge>
                        ))}
                      </div>
                    </div>
                    
                    <div className="p-3 bg-[#F5F5FA] rounded-lg text-sm">
                      <p className="font-medium text-[#1C1F1D] mb-1">Will Post:</p>
                      <p className="text-[#5C635F]">
                        {config.tally_type === 'A' 
                          ? 'Invoices, Cash Payments, Credit Notes, Bank Recon'
                          : 'Invoices, Credit Notes, Bank Recon (No Cash)'}
                      </p>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => testConnection(config.id)}
                        disabled={testing[config.id]}
                      >
                        {testing[config.id] ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wifi className="w-4 h-4 mr-2" />}
                        Test Connection
                      </Button>
                      <Button 
                        className="flex-1 bg-[#3E3F96] hover:bg-[#2D2E7A]"
                        onClick={() => postToTally(config.id, config.name)}
                        disabled={posting[config.id]}
                        data-testid={`post-btn-${config.id}`}
                      >
                        {posting[config.id] ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                        Post Now
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Posting History Tab */}
        <TabsContent value="history">
          <Card className="border-[#D8D9E8]">
            <CardHeader>
              <CardTitle className="text-lg">Posting History</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {postingLogs.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 text-[#5C635F]">
                  <History className="w-12 h-12 opacity-50 mb-4" />
                  <p>No posting history yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F5F5FA]">
                      <TableHead className="text-xs uppercase font-semibold">Date</TableHead>
                      <TableHead className="text-xs uppercase font-semibold">Configuration</TableHead>
                      <TableHead className="text-xs uppercase font-semibold">Type</TableHead>
                      <TableHead className="text-xs uppercase font-semibold">Branches</TableHead>
                      <TableHead className="text-xs uppercase font-semibold">Status</TableHead>
                      <TableHead className="text-xs uppercase font-semibold">Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {postingLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="text-[#5C635F]">{formatDate(log.posted_at)}</TableCell>
                        <TableCell className="font-medium">{log.config_name}</TableCell>
                        <TableCell>
                          <Badge className={log.tally_type === 'A' ? 'bg-[#3E3F96]/10 text-[#3E3F96]' : 'bg-[#D99C3B]/10 text-[#D99C3B]'}>
                            Tally {log.tally_type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {log.branches?.slice(0, 2).map((b) => (
                              <Badge key={b} variant="outline" className="text-xs">{b}</Badge>
                            ))}
                            {log.branches?.length > 2 && (
                              <Badge variant="outline" className="text-xs">+{log.branches.length - 2}</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {log.status === 'success' ? (
                            <div className="flex items-center gap-1 text-[#5556A8]">
                              <CheckCircle className="w-4 h-4" />
                              <span className="text-sm">Success</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-[#C85A40]">
                              <XCircle className="w-4 h-4" />
                              <span className="text-sm">Failed</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-[#5C635F]">
                          {log.counts ? (
                            <span>
                              Inv: {log.counts.invoices || 0}, 
                              Pay: {log.counts.payments || 0}, 
                              CN: {log.counts.credit_notes || 0}, 
                              Bank: {log.counts.bank_transactions || 0}
                            </span>
                          ) : log.error ? (
                            <span className="text-[#C85A40]">{log.error.substring(0, 50)}...</span>
                          ) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TallySetupPage;
